import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Heart, Cookie, Settings, Globe } from 'lucide-react';
import { usePrivacy, ConsentLevel } from './PrivacyConsent';
import { useClubBranding } from '../context/ClubBrandingContext';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';

/**
 * Cookie Preferences Modal
 * Allows users to change their privacy settings at any time
 */
const CookiePreferencesModal = ({ open, onOpenChange }) => {
  const { consentLevel, setConsentLevel, consentTimestamp, consentExpiry, isEuUser } = usePrivacy();

  const formatDate = (date) => {
    if (!date) return 'Not set';
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const options = [
    { 
      level: ConsentLevel.ESSENTIAL, 
      label: 'Essential Only', 
      desc: 'No ads, no tracking. Only cookies required for the site to function.',
      color: 'text-green-600',
      bgColor: 'bg-green-50 border-green-200'
    },
    { 
      level: ConsentLevel.LIMITED, 
      label: 'Privacy-Friendly', 
      desc: 'Non-personalized ads with minimal analytics. No cross-site tracking.',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50 border-blue-200'
    },
    { 
      level: ConsentLevel.FULL, 
      label: 'Personalized', 
      desc: 'Personalized ads based on your browsing interests.',
      color: 'text-amber-600',
      bgColor: 'bg-amber-50 border-amber-200',
      hidden: isEuUser
    }
  ].filter(opt => !opt.hidden);

  const handleSelect = (level) => {
    setConsentLevel(level);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="cookie-preferences-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Cookie className="w-5 h-5 text-primary" />
            Cookie Preferences
          </DialogTitle>
          <DialogDescription>
            Control how we use cookies and tracking technologies. Your choice is saved locally and synced with our servers.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-3 py-4">
          {options.map(({ level, label, desc, color, bgColor }) => (
            <button
              key={level}
              onClick={() => handleSelect(level)}
              className={`w-full text-left p-4 rounded-lg border transition-all ${
                consentLevel === level 
                  ? `${bgColor} border-2` 
                  : 'bg-muted/30 hover:bg-muted border-transparent'
              }`}
              data-testid={`cookie-option-${level}`}
            >
              <div className="flex items-center justify-between">
                <span className={`font-medium ${color}`}>{label}</span>
                {consentLevel === level && (
                  <span className="text-xs bg-primary text-primary-foreground px-2 py-0.5 rounded-full">
                    Current
                  </span>
                )}
              </div>
              <p className="text-sm text-muted-foreground mt-1">{desc}</p>
            </button>
          ))}
        </div>

        <div className="text-xs text-muted-foreground border-t pt-4 space-y-1">
          <p><strong>Consent given:</strong> {formatDate(consentTimestamp)}</p>
          <p><strong>Expires:</strong> {formatDate(consentExpiry)}</p>
          <p className="pt-2">
            <Link 
              to="/privacy-policy" 
              className="text-primary hover:underline"
              onClick={() => onOpenChange(false)}
            >
              View full Privacy Policy →
            </Link>
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const PrivacyBanner = () => {
  const branding = useClubBranding();
  const siteName = branding.isWorldPets ? 'WorldPets Global' : branding.siteName + ' ' + branding.siteNameSecondary;
  
  return (
    <div className="bg-muted/50 border-t py-4">
      <div className="container mx-auto px-4">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-primary" />
            <span>
              {siteName} is <strong>in the process of establishing nonprofit status</strong>. 
              We never sell your personal data and will never be sold to a commercial company.
            </span>
          </div>
          <Link 
            to="/privacy" 
            className="text-primary hover:underline flex items-center gap-1"
          >
            Learn about our privacy practices →
          </Link>
        </div>
      </div>
    </div>
  );
};

export const Footer = () => {
  const [cookieModalOpen, setCookieModalOpen] = useState(false);
  const { hasConsented, consentLevel } = usePrivacy();
  const branding = useClubBranding();
  const siteName = branding.siteName + ' ' + branding.siteNameSecondary;
  const aboutLabel = branding.isWorldPets ? 'About WorldPets' : branding.isAADC ? 'About AADC' : 'About Us';

  return (
    <footer className="bg-card border-t mt-auto">
      <PrivacyBanner />
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {/* About */}
          <div>
            <h4 className="font-heading font-medium mb-3">{aboutLabel}</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><Link to="/about" className="hover:text-primary">About Us</Link></li>
              <li><Link to="/features" className="hover:text-primary">Features & Benefits</Link></li>
              <li><Link to="/club-blog" className="hover:text-primary">Club News</Link></li>
              <li><Link to="/privacy-policy" className="hover:text-primary">Privacy Policy</Link></li>
              <li><Link to="/disclaimer" className="hover:text-primary">Disclaimer</Link></li>
              <li><a href="#" className="hover:text-primary">Terms of Service</a></li>
              <li><Link to="/faq" className="hover:text-primary">FAQ</Link></li>
            </ul>
          </div>
          
          {/* Community */}
          <div>
            <h4 className="font-heading font-medium mb-3">Community</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><Link to="/forums" className="hover:text-primary">Forums</Link></li>
              <li><Link to="/events" className="hover:text-primary">Events</Link></li>
              <li><Link to="/groups" className="hover:text-primary">Groups</Link></li>
              <li><Link to="/organizations" className="hover:text-primary">Clubs & Organizations</Link></li>
              <li><Link to="/request-club" className="hover:text-primary">Request a Club</Link></li>
              <li><Link to="/beta-feedback" className="hover:text-primary">Feedback & Updates</Link></li>
            </ul>
          </div>
          
          {/* Resources */}
          <div>
            <h4 className="font-heading font-medium mb-3">Resources</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><Link to="/breeds" className="hover:text-primary">Breed Database</Link></li>
              <li><Link to="/services" className="hover:text-primary">Find Services</Link></li>
              <li><Link to="/parks" className="hover:text-primary">Dog Parks</Link></li>
              <li><Link to="/health" className="hover:text-primary">Dog Health</Link></li>
              <li><Link to="/care-team" className="hover:text-primary">My Care Team</Link></li>
              <li><Link to="/deals" className="hover:text-primary">Partner Deals</Link></li>
            </ul>
          </div>
          
          {/* Support */}
          <div>
            <h4 className="font-heading font-medium mb-3">Partner With Us</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><Link to="/contact?type=sponsor" className="hover:text-primary">Become a Sponsor</Link></li>
              <li><Link to="/contact?type=partner" className="hover:text-primary">Regional Partnership</Link></li>
              <li><Link to="/contact?type=advertiser" className="hover:text-primary">Advertise With Us</Link></li>
              <li><Link to="/contact?type=ngo" className="hover:text-primary">NGO Partnership</Link></li>
              <li><Link to="/contact" className="hover:text-primary">Contact Us</Link></li>
              <li><Link to="/about/future" className="hover:text-primary">Our Vision</Link></li>
            </ul>
          </div>
        </div>
        
        <div className="mt-8 pt-8 border-t flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Heart className="w-4 h-4 text-secondary" />
              <span className="text-sm text-muted-foreground">
                Made with love for pets everywhere
              </span>
            </div>
            
            {/* WorldPets Global Network */}
            <a
              href="https://worldpets.club"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors px-2 py-1 rounded-md hover:bg-muted"
              data-testid="worldpets-link"
            >
              <Globe className="w-3 h-3" />
              <span>WorldPets Global Network</span>
            </a>
            
            {/* Cookie Preferences Button */}
            <button
              onClick={() => setCookieModalOpen(true)}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors px-2 py-1 rounded-md hover:bg-muted"
              data-testid="cookie-preferences-btn"
            >
              <Cookie className="w-3 h-3" />
              <span>Cookie Preferences</span>
              {hasConsented && (
                <span className={`ml-1 w-2 h-2 rounded-full ${
                  consentLevel === ConsentLevel.ESSENTIAL ? 'bg-green-500' :
                  consentLevel === ConsentLevel.LIMITED ? 'bg-blue-500' :
                  'bg-amber-500'
                }`} />
              )}
            </button>
          </div>
          
          <p className="text-sm text-muted-foreground">
            © 2025 {siteName}. In the process of establishing 501(c)(3) nonprofit status.
          </p>
        </div>
      </div>
      
      {/* Cookie Preferences Modal */}
      <CookiePreferencesModal 
        open={cookieModalOpen} 
        onOpenChange={setCookieModalOpen} 
      />
    </footer>
  );
};

export default Footer;
